import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useStaticData } from '@mc/static-data-react'
// Deliberate coupling: when this experiment is cleaned up or replaced, this
// import breaks and whoever does it has to re-decide the interaction below.
import { useSkinnyCmExperiment } from 'experiments/pen-6957_v2_cm_skinny_cm'
import { type PrunedCourse } from 'libs/server/api'
import {
  TrailerModalLocations,
  trackTrailerDeeplinkPlaybackStarted,
} from '../events'
import {
  CourseTrailerModal,
  getCourseTrailerProps,
  hasCourseTrailer,
} from './CourseTrailerModal'

const OPEN_TRAILER_QUERY_PARAM = 'open_trailer'
const OPEN_TRAILER_QUERY_VALUE = 'true'

/**
 * Must stay at page level rather than in `TrailerAction`: the hero mounts
 * `TrailerAction` twice — desktop and mobile are both mounted and only hidden
 * by CSS — so auto-opening there would start two modals and two videos at once.
 */
export const TrailerDeeplinkGate = () => {
  const router = useRouter()
  const { course } = useStaticData<{ course: PrunedCourse }>()
  const { shouldShow: skinnyCmModalOpens } = useSkinnyCmExperiment()
  const [shouldShow, setShouldShow] = useState(false)
  const hasHandledDeeplink = useRef(false)
  const hasTrackedPlayback = useRef(false)

  useEffect(() => {
    // `open_trailer` is stripped on close, not on open, and the strip is async,
    // so the parameter outlives the close by a render or two. Next also rebuilds
    // the public router instance on every root render. Without this guard the
    // effect would reopen a modal the user has already closed.
    if (hasHandledDeeplink.current) return
    if (router.query[OPEN_TRAILER_QUERY_PARAM] !== OPEN_TRAILER_QUERY_VALUE)
      return
    if (!hasCourseTrailer(course)) return
    // The Skinny CM experiment auto-opens its own modal with its own autoplaying
    // video on this page. Two of them race and stack, so the deeplink stands
    // down rather than fight it.
    if (skinnyCmModalOpens) return

    hasHandledDeeplink.current = true
    setShouldShow(true)
  }, [course, router, skinnyCmModalOpens])

  if (!shouldShow) return null

  const handlePlay = () => {
    if (hasTrackedPlayback.current) return

    hasTrackedPlayback.current = true
    trackTrailerDeeplinkPlaybackStarted({ slug: course.slug || '' })
  }

  const handleClose = () => {
    setShouldShow(false)

    const { [OPEN_TRAILER_QUERY_PARAM]: _stripped, ...rest } = router.query
    router.replace({ pathname: router.pathname, query: rest }, undefined, {
      shallow: true,
    })
  }

  return (
    <CourseTrailerModal
      trailer={getCourseTrailerProps(course)}
      location={TrailerModalLocations.DEEPLINK}
      onClose={handleClose}
      onPlay={handlePlay}
    />
  )
}
